<script setup lang="ts">
import { computed, onBeforeMount, onMounted, shallowRef } from 'vue';
import { store } from '@/store/store';
import Uploader from '@/modules/common/components/Uploader.vue';
import UploadConfirmBasket from '@/modules/common/components/UploadConfirmBasket.vue';
import type { UploadMethod } from '@/modules/common/types/upload';
import { RiskLimitValidator } from '@/utils/helpers/risk-limit-validator';
import Decimal from 'decimal.js';
import {
  CreateOmsBasketRequest,
  ParseOmsBasketRequest,
  ParseOmsBasketResponse,
} from '@/modules/marketplace/models';
import { ErrorItem, TimeTuple } from '@/modules/common/models';
import { OmsBasketItem } from '@/modules/marketplace/models';
import { Side } from '@/connect/gen/consts/commonconsts_pb';
import MarketplaceOrdersUploadBasketFields from '@/modules/marketplace/components/MarketplaceOrdersUploadBasketFields.vue';
import { OrderIntent, OrderType, TimeInForceType } from '@/modules/marketplace/types/marketplace';
import { AgreementInfo } from '@/modules/agreements/models';
import { addHours, setHours, setMinutes, setSeconds } from 'date-fns';
import { RateType } from '@/connect';
import { useRateTypeStore } from '@/store/rate-type-store';
import { useAgreementsStore } from '@/store/agreements-store';
import * as cols from '@/modules/common/components/ag-table/columns/marketplace-orders-upload';
import { SponsoredLoanDetailsResponseItem } from '@/modules/sponsorship/models';
import { useDialogManager } from '@/plugins/dialog-manager';
import { useMarketplaceApiService } from '@/modules/marketplace/services/marketplace-api.service';
import { useCurrentTime } from '@/composables/current-time';

const emit = defineEmits<{ (e: 'close-modal'): void }>();

const props = defineProps<{ side: Side }>();

const clientConfig = computed(() => store.state.clientConfig);
const loginState = computed(() => store.state.loginState);
const { currentTimeUTC } = useCurrentTime();

const marketplaceApiService = useMarketplaceApiService();
const dialogManager = useDialogManager();
const agreementsStore = useAgreementsStore();
const rateTypeStore = useRateTypeStore();

const riskLimitValidator = shallowRef<RiskLimitValidator | null>(null);

const orderType = shallowRef<OrderType>('LIMIT');
const orderIntent = shallowRef<OrderIntent>('FIRM');
const agreements = shallowRef<AgreementInfo[]>([]);
const timeInForceType = shallowRef<TimeInForceType>('DAY');
const goodTillTime = shallowRef<TimeTuple>([12, 0]);
const allowOddLots = shallowRef(true);

const uploadMethod = shallowRef<UploadMethod | null>(null);

const step = shallowRef<'upload' | 'confirm'>('upload');
const basketResponse = shallowRef<ParseOmsBasketResponse>();
const errors = shallowRef<ErrorItem[]>([]);

const anyEntriesExceedSoftLimit = computed(() =>
  basketResponse.value?.entries.some(entryExceedsSoftLimit)
);

const nextHour = computed(() => setMinutes(addHours(currentTimeUTC.value, 1), 0));

const dataColDefs = computed(() => [
  cols.security(),
  cols.quantity(),
  cols.rate(),
  cols.rateType(),
  cols.active(),
  cols.minQuantity(),
  cols.orderType(),
  cols.orderIntent(),
  cols.timeInForceType(),
  cols.agreements(),
  ...(anyEntriesExceedSoftLimit.value
    ? [
        cols.exceedsSoftLimitMessage({
          softLimitValue: riskLimitValidator.value!.softLimitValue,
          entryExceedsSoftLimit: entryExceedsSoftLimit,
        }),
      ]
    : []),
]);

const errorColDefs = computed(() => [cols.errorRow(), cols.errorField(), cols.errorMessage()]);

onBeforeMount(() => {
  goodTillTime.value = [nextHour.value.getHours(), 0];
});

onMounted(() => {
  if (!loginState.value.user) return;
  riskLimitValidator.value = new RiskLimitValidator(dialogManager, loginState.value.user);

  agreements.value =
    loginState.value.user?.companyDefaultOmsAgreements.filter(
      (agreement) => agreementsStore.getAgreement(agreement.id)?.side == props.side
    ) || [];
});

async function executeUpload(file: File): Promise<ParseOmsBasketResponse> {
  return await marketplaceApiService.uploadOrders(
    ParseOmsBasketRequest.fromData({
      side: props.side === Side.LOAN ? 'LOAN' : 'BORROW',
      orderType: orderType.value,
      orderIntent: orderIntent.value,
      agreementIds: agreements.value.map((c) => c.id),
      timeInForceType: timeInForceType.value,
      goodTillTime:
        timeInForceType.value == 'GOOD_TILL_TIME'
          ? setSeconds(
              setMinutes(
                setHours(currentTimeUTC.value, goodTillTime.value[0]),
                goodTillTime.value[1]
              ),
              0
            ).toISOString()
          : null,
      file,
      rateType: RateType[rateTypeStore.ordersUploadRateType] as 'REBATE' | 'FEE',
      active: true,
      allowOddLots: allowOddLots.value,
    })
  );
}

function goToConfirm(response: ParseOmsBasketResponse): void {
  basketResponse.value = response;
  step.value = 'confirm';
}

function goToConfirmWithErrors(response: ParseOmsBasketResponse): void {
  basketResponse.value = response;
  errors.value = response.errors ?? [];
  step.value = 'confirm';
}

function closeDialog(): void {
  emit('close-modal');
}

function entryExceedsSoftLimit(entry: SponsoredLoanDetailsResponseItem | OmsBasketItem): boolean {
  return riskLimitValidator.value!.isGreaterThanOrderSoftLimit(
    entry.security.lastClosePrice.mul(new Decimal(entry.quantity))
  );
}

async function executeRequest(response: ParseOmsBasketResponse): Promise<void> {
  const request = CreateOmsBasketRequest.fromParseOmsBasketResponse(response);
  await marketplaceApiService.createOrders(request);
}
</script>

<template>
  <VDialog
    v-shortkey="['esc']"
    content-class="au-popup-dialog"
    max-width="850"
    overlay-color="secondary"
    overlay-opacity="0.80"
    persistents
    scrollable
    :value="true"
    @click:outside="closeDialog()"
    @keydown.esc="closeDialog()"
    @shortkey.native="closeDialog()"
  >
    <Uploader
      v-if="step === 'upload'"
      :execute="executeUpload"
      :side="Side[side]"
      :with-dummy-header="false"
      @close-modal="closeDialog()"
      @upload="goToConfirm($event)"
      @upload-method="uploadMethod = $event"
      @upload-with-errors="goToConfirmWithErrors($event)"
    >
      <template #title>
        Upload
        <FormatSide :side="Side[side]">
          {{ side === Side.LOAN ? 'Supply' : 'Demand' }}
        </FormatSide>
        orders
      </template>

      <template #basket-fields>
        <MarketplaceOrdersUploadBasketFields
          :agreements.sync="agreements"
          :allow-odd-lots.sync="allowOddLots"
          :good-till-time.sync="goodTillTime"
          :order-intent.sync="orderIntent"
          :order-type="orderType"
          :rate-type.sync="rateTypeStore.ordersUploadRateType"
          :side="side"
          :time-in-force-type.sync="timeInForceType"
        />
      </template>

      <template #description>
        <div>Expected columns (* is optional):</div>
        <div>
          {{
            [
              'Ticker or CUSIP',
              'Quantity',
              'Rate',
              'Agreements*',
              'Active*',
              clientConfig?.orderbookMinQuantityEditable ? 'Min. Quantity*' : null,
            ]
              .filter((f) => !!f)
              .join(' | ')
          }}
        </div>
      </template>
    </Uploader>

    <UploadConfirmBasket
      v-if="step === 'confirm'"
      :basket-response="basketResponse"
      :data-col-defs="dataColDefs"
      :error-col-defs="errorColDefs"
      :execute="executeRequest"
      timeframe="createLoans"
      :upload-method="uploadMethod"
      @back="step = 'upload'"
      @close-modal="closeDialog()"
    >
      <template #title
        >New
        <FormatSide :side="basketResponse?.side" />
        orders
      </template>
      <template v-if="loginState.user" #summary>
        You are about to create <strong>{{ basketResponse?.entries.length }}</strong> orders
      </template>
      <template #create-button-text>Create {{ basketResponse?.entries.length }} orders</template>
      <template #success-message>Orders successfully created</template>
      <template #error-title
        >Failed
        <FormatSide :side="Side[side]" />
        orders
      </template>
    </UploadConfirmBasket>
  </VDialog>
</template>
